import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SignalProvider } from '../realtime/signal-provider.interface';
import { SignalMap } from '../realtime/signal.types';
import { PermissionService } from './permission.service';
import { PermissionSyncPayload, PERMISSION_STORAGE_KEYS } from './permission.types';
import { authApiResources } from '../../modules/auth/auth.constants';
import { SKIP_FORBIDDEN_REDIRECT } from '../../interceptors/http-context-tokens';
import { KEEP_ALIVE } from '../../interceptors/cancel-on-navigation.interceptor';

/** Signal Bus scope carrying permission-change events. */
export const PERMISSION_SIGNAL_SCOPE = 'permissions';

/**
 * Keeps the in-memory permission store in sync with the backend, in real time,
 * without logging the user out.
 *
 * Flow: the backend writes `signals/family_{root}/permissions` as
 * `{ role_7: <ms>, user_42: <ms> }`. Every connected tab receives the node, but
 * only a tab whose user actually holds role 7 (or IS user 42) calls the API —
 * everyone else does nothing at all. That client-side filter is what keeps a
 * role change from stampeding the API with one request per logged-in user.
 *
 * The signal carries no permission data. It only says "something changed"; the
 * permissions themselves always come from the authenticated REST endpoint.
 */
@Injectable({ providedIn: 'root' })
export class PermissionSyncService implements OnDestroy {
  /** Spread simultaneous refreshes from users of the same role. */
  private static readonly MAX_JITTER_MS = 3000;
  private static readonly MAX_RETRIES = 3;

  private channel: BroadcastChannel | null = null;
  private signalSub: Subscription | null = null;
  private pendingTimer: any = null;
  private retryTimer: any = null;
  private userId: number | null = null;
  private started = false;

  private onStorage = (event: StorageEvent): void => {
    if (event.key === PERMISSION_STORAGE_KEYS.permissions) {
      this.permissions.rehydrateFromStorage();
    }
  };

  private onVisibility = (): void => {
    // A tab that was hidden may have missed a signal whose key has since been
    // pruned from the node. Cheap to re-check when it comes back.
    if (!document.hidden && this.started) {
      this.refreshNow();
    }
  };

  constructor(
    private signalProvider: SignalProvider,
    private permissions: PermissionService,
    private http: HttpClient
  ) {}

  /**
   * Begin syncing. Call after login, and again after an office switch (the
   * family root changes, so the node we listen on changes with it).
   */
  start(familyRootId: number, userId: number): void {
    if (!familyRootId || !userId) {
      return;
    }
    if (this.started) {
      this.stop();
    }

    this.userId = Number(userId);
    this.started = true;

    this.signalProvider.connect(familyRootId, PERMISSION_SIGNAL_SCOPE);
    this.signalSub = this.signalProvider.changes$
      .pipe(filter((change) => change.scope === PERMISSION_SIGNAL_SCOPE))
      .subscribe((change) => this.onSignal(change.changed));

    this.openChannel();
    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisibility);

    // Unconditional refresh on start. Besides covering a signal missed while
    // disconnected, this closes a long-standing gap: permissions used to be
    // fetched only by LoginComponent, so a reloaded tab ran on whatever was in
    // localStorage indefinitely.
    this.refreshNow();
  }

  /** Stop syncing and release listeners. Call on logout / 401 / office switch. */
  stop(): void {
    this.started = false;
    this.userId = null;

    this.cancelPending();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.signalSub) {
      this.signalSub.unsubscribe();
      this.signalSub = null;
    }
    this.signalProvider.disconnect(PERMISSION_SIGNAL_SCOPE);

    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onVisibility);

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
  }

  // ── Signal handling ────────────────────────────────────────────────────

  /**
   * Decide whether this signal is ours. Returns without touching the network
   * for every user whose roles were not modified — the whole point of keying
   * the node by role rather than fanning out per user.
   */
  private onSignal(changed: SignalMap): void {
    if (!this.started) {
      return;
    }

    const myRoleIds = this.permissions.roleIds();
    const affected = Object.keys(changed || {}).some((key) => {
      if (key.startsWith('role_')) {
        return myRoleIds.includes(Number(key.slice(5)));
      }
      if (key.startsWith('user_')) {
        return Number(key.slice(5)) === this.userId;
      }
      return false;
    });

    if (!affected) {
      return; // Not our change. No API call.
    }

    // Jitter serves double duty. Across users it spreads a role-wide stampede;
    // across tabs of the SAME user it IS the leader election — the shortest
    // delay wins, fetches, and broadcasts the result, which cancels the others.
    // (An explicit claim/ack handshake would deadlock: two tabs claiming in the
    // same tick would each cancel the other and nobody would fetch.)
    this.cancelPending();
    const jitter = Math.floor(Math.random() * PermissionSyncService.MAX_JITTER_MS);
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      this.refreshNow();
    }, jitter);
  }

  // ── Fetching ───────────────────────────────────────────────────────────

  /**
   * One-shot fetch of the current permission payload, independent of the bus.
   * Used to bootstrap a reloaded tab whose cached auth_detail is missing — the
   * response carries the family_root_id needed to know which node to listen on.
   */
  probe(): Observable<{ success: boolean; data: PermissionSyncPayload }> {
    return this.request();
  }

  /**
   * loadingService: false — this is a background refresh; it must never put the
   * global loading spinner over whatever the user is doing.
   *
   * SKIP_FORBIDDEN_REDIRECT — a failure here must not bounce the user off the
   * page they are working on; we retry quietly instead.
   *
   * KEEP_ALIVE — REQUIRED. CancelOnNavigationInterceptor cancels every GET on
   * NavigationStart via takeUntil, which COMPLETES the stream without emitting
   * a value or an error. A permission refresh caught by a navigation would
   * therefore be dropped silently: no apply, and not even a retry, since the
   * error handler never runs. That is guaranteed at bootstrap (the router
   * navigates immediately after start()) and likely whenever a signal lands
   * while the user is clicking around — exactly when permissions matter most.
   */
  private request(): Observable<{ success: boolean; data: PermissionSyncPayload }> {
    return this.http.get<{ success: boolean; data: PermissionSyncPayload }>(
      authApiResources.userPermissionsSync,
      {
        params: { loadingService: false } as any,
        context: new HttpContext()
          .set(SKIP_FORBIDDEN_REDIRECT, true)
          .set(KEEP_ALIVE, true),
      }
    );
  }

  private refreshNow(attempt = 0): void {
    if (!this.started) {
      return;
    }

    this.request()
      .subscribe({
        next: (response) => {
          const payload = response?.data;
          if (!payload) {
            return;
          }
          this.userId = Number(payload.user_id) || this.userId;
          if (this.permissions.apply(payload)) {
            this.publishResult(payload);
          }
        },
        error: () => {
          // Never clear permissions on failure — the user would lose UI they are
          // still authorized for, and the backend enforces regardless.
          if (attempt < PermissionSyncService.MAX_RETRIES) {
            const backoff = 1000 * Math.pow(2, attempt);
            this.retryTimer = setTimeout(() => {
              this.retryTimer = null;
              this.refreshNow(attempt + 1);
            }, backoff);
          }
        },
      });
  }

  // ── Cross-tab coordination ─────────────────────────────────────────────

  /**
   * Tabs each hold their own Signal Bus listener, so all of them see the event.
   * Whichever tab's jitter fires first fetches and broadcasts the payload; the
   * others drop their pending fetch and apply the shared result. One API call
   * per user, not per tab.
   *
   * A near-simultaneous double fetch is possible and harmless — the second
   * response is either identical or newer, and PermissionService.apply() drops
   * anything stale by issued_at.
   */
  private openChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return; // Falls back to the `storage` event, which still propagates values.
    }

    this.channel = new BroadcastChannel('permission-sync');
    this.channel.addEventListener('message', (event: MessageEvent) => {
      const message = event?.data;
      if (!message || message.type !== 'result' || !message.payload) {
        return;
      }
      // Two different users can be logged in across tabs (or a tab may be
      // mid-logout). Never apply another user's permissions.
      if (Number(message.userId) !== this.userId) {
        return;
      }
      this.cancelPending();
      this.permissions.apply(message.payload);
    });
  }

  private publishResult(payload: PermissionSyncPayload): void {
    this.channel?.postMessage({ type: 'result', payload, userId: this.userId });
  }

  private cancelPending(): void {
    if (this.pendingTimer) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
